<template>
  <div
    v-if="isGraphTypeLinechart"
    class="sf-bg-snow-lighter sf-pad-4 sf-below-2"
    data-walkthrough-id="chart-xaxis-selector"
  >
    <div class="x-axis-selector">
      <div class="sf-uppercase sf-below-1">
        {{ I18n.t('js.report_graph.x_axis.title') }}
      </div>

      <div
        v-show="rangeErrors"
        class="sf-alert sf-alert-danger sf-pad-1 sf-level-vertical sf-below-2"
      >
        <div
          v-for="(errorMessage, index) in rangeErrors"
          :key="index"
          class="sf-level-item"
        >
          {{ I18n.t(`js.report_graph.edit.x_axis_options.empty_range_error`) }}
        </div>
      </div>

      <div
        class="
          sf-level
          sf-level-between
          sf-level-none
          sf-align-c
          sf-layer-border
          sf-layer-rounded
          sf-font-xs
          sf-fwdb
          sf-c-text-dark
          sf-pointer
          sf-below-2
        "
        @mouseleave="resetDrag"
        @mouseup="resetDrag"
      >
        <div
          v-for="{ year, displayYear } in years"
          :key="year"
          :class="classesForYear(year)"
          class="sf-level-item sf-layer-rounded sf-pad-1"
          :year="year"
          @mousedown.prevent="enterDrag(year)"
          @mouseenter.prevent="dragging(year)"
          @mouseup.prevent="resetDrag"
          @click.prevent="processClick(year)"
        >
          {{ displayYear }}
        </div>
      </div>

      <xaxis-options />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import xaxisOptions from './xaxis-options.vue'

export default {
  components: { xaxisOptions },

  data() {
    return {
      pushed: false,
      start: null,
    }
  },

  computed: {
    ...mapState(['readOnly', 'graphPeriods']),
    ...mapGetters(['graphErrors', 'isGraphTypeLinechart']),

    rangeStart() {
      return this.graphPeriods.range.start
    },

    rangeEnd() {
      return this.graphPeriods.range.end
    },

    rangeErrors() {
      if (this.graphErrors) {
        return this.graphErrors['reportGraphPeriods.delta']
      }

      return false
    },

    years() {
      const yearSuffix = I18n.t('js.report_graph.x_axis.year_suffix')
      const currentYear = I18n.t('js.report_graph.x_axis.current_year')

      return Array(11)
        .fill()
        .map((x, i) => {
          const year = i - 5 // Years go from -5 to +5
          const displayYear =
            year === 0
              ? currentYear
              : `${year < 0 ? '-' : '+'} ${Math.abs(year)} ${yearSuffix}`

          return { year, displayYear }
        })
    },
  },

  updated() {
    this.updateVisualShadow()
  },
  mounted() {
    this.updateVisualShadow()
  },

  methods: {
    ...mapActions(['setXAxisValue']),

    classesForYear(year) {
      return {
        'selected sf-bg-primary sf-c-white':
          year === this.rangeStart || year === this.rangeEnd,
        'selected sf-bg-primary-subtle sf-c-primary':
          this.rangeEnd !== null &&
          year > this.rangeStart &&
          year < this.rangeEnd,
        'small-element': year !== 0,
        'big-element': year === 0,
      }
    },

    processClick(year) {
      if (this.readOnly) return null

      if (
        this.rangeStart !== null &&
        this.rangeEnd === null &&
        year > this.rangeStart
      ) {
        this.commitRange(this.rangeStart, year)
      } else if (
        this.rangeStart !== null &&
        this.rangeEnd === null &&
        year === this.rangeStart
      ) {
        this.commitStartRange(null)
      } else {
        this.commitStartRange(year)
      }
    },

    processDrag(start, end) {
      if (this.readOnly) return null

      if (start === this.rangeStart) {
        this.commitRange(
          end,
          this.rangeEnd === null ? this.rangeStart : this.rangeEnd
        )
      } else if (start === this.rangeEnd) {
        this.commitRange(end, this.rangeStart)
      } else {
        this.commitRange(start, end)
      }

      this.start = end
    },

    enterDrag(year) {
      this.pushed = true
      this.start = year
    },

    dragging(year) {
      if (this.pushed) {
        this.processDrag(this.start, year)
      }
    },

    resetDrag() {
      this.pushed = false
    },

    commitStartRange(newStartRange) {
      if (this.readOnly) return null

      this.setXAxisValue({ range: { start: newStartRange, end: null } })
    },

    commitRange(newStartRange, newEndRange) {
      if (this.readOnly) return null

      const start = newEndRange > newStartRange ? newStartRange : newEndRange
      const end = newEndRange > newStartRange ? newEndRange : newStartRange
      this.setXAxisValue({ range: { start: start, end: end } })
    },

    updateVisualShadow() {
      const elements = [...document.querySelectorAll('.selected')]
      if (!elements || !elements[0]) return null

      // Otherwise the first element would always have no box-shadow
      elements[0].classList.remove('no-shadow')
      elements[elements.length - 1].classList.add('no-shadow')
    },
  },
}
</script>
